import React from "react";
import { Seeker } from "../../types/seeker";

interface SeekerOnboardingProgressProps {
    seeker: Seeker;
}

function OnboardingProgressStep({
    title,
    description,
    isCompleted,
    isLast,
}: {
    title: string;
    description: string;
    isCompleted: boolean;
    isLast?: boolean;
}) {
    return (
        <div
            className={[
                "flex items-start justify-between gap-4 py-3",
                isLast ? "" : "border-b border-[#2C2731]/15",
            ]
                .filter(Boolean)
                .join(" ")}
        >
            <div>
                <div className="text-sm font-semibold text-[#2C2731]">{title}</div>
                <div className="text-xs text-[#403851]">{description}</div>
            </div>
            <span
                className={[
                    "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em]",
                    isCompleted ? "bg-[#2C2731] text-[#FEF6D6]" : "bg-[#F2EAC3] text-[#403851]",
                ].join(" ")}
            >
                {isCompleted ? "Completed" : "Pending"}
            </span>
        </div>
    );
}

export default function SeekerOnboardingProgress({ seeker }: SeekerOnboardingProgressProps) {
    // GCS-214: all post-onboarding derived statuses imply onboarding_status
    // was "completed" at the DB level, so they all satisfy every step of the
    // progress indicator. Listed here explicitly so adding a new derived
    // status in future is a compile error until it's considered here.
    const POST_ONBOARDING: string[] = [
        "completed",
        "consultation_scheduled",
        "consultation_completed",
        "consultation_missed",
        "awaiting_package",
        "package_purchased",
        "package_delivered",
        "package_complete",
    ];
    const isBasicInfoCompleted = ["basic_info_completed", "experience_preferences_completed", ...POST_ONBOARDING].includes(seeker.onboardingStatus);
    const isExperienceCompleted = ["experience_preferences_completed", ...POST_ONBOARDING].includes(seeker.onboardingStatus);
    const isFinalCompleted = POST_ONBOARDING.includes(seeker.onboardingStatus);
    const isConsultationScheduled = seeker.onboardingStatus === "consultation_scheduled";

    const getStartedDescription = () => {
        const startDate = seeker.onboardingStartedAt 
            ? new Date(seeker.onboardingStartedAt).toLocaleDateString()
            : new Date(seeker.joinedAt).toLocaleDateString();
        const sessionId = seeker.sessionId ? ` • Session ID: ${seeker.sessionId}` : "";
        return `${startDate}${sessionId}`;
    };

    const getBasicInfoDescription = () => {
        const ageVerification = seeker.seekerOver18 === 'Yes' ? "✓ Over 18" : "⚠ Under 18";
        const genderDisplay = seeker.seekerGenders && seeker.seekerGenders.length > 0
            ? seeker.seekerGenders.join(", ")
            : seeker.seekerGender;
        const gender = genderDisplay ? ` • Gender: ${genderDisplay}` : "";
        return `Age verification: ${ageVerification}${gender}`;
    };

    const getExperienceDescription = () => {
        const count = seeker.experienceTypes?.length || 0;
        return count > 0 ? `${count} experience type(s) selected` : "No experience types selected";
    };

    const getFinalDescription = () => {
        return seeker.onboardingCompletedAt
            ? `Completed: ${new Date(seeker.onboardingCompletedAt).toLocaleDateString()}`
            : "Medical information and final details";
    };

    const getConsultationDescription = () => {
        return seeker.consultationScheduledAt
            ? `Scheduled: ${new Date(seeker.consultationScheduledAt).toLocaleDateString()}`
            : "Consultation with matched guide";
    };

    return (
        <section className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-[inset_0_0_0_1px_rgba(44,39,49,0.05)]">
            <h4 className="mb-4 text-sm font-semibold uppercase tracking-[0.12em] text-[#2C2731]">
                Onboarding Progress
            </h4>

            <div className="space-y-2">
                <OnboardingProgressStep
                    title="Onboarding Started"
                    description={getStartedDescription()}
                    isCompleted={true}
                />
                <OnboardingProgressStep
                    title="Basic Information"
                    description={getBasicInfoDescription()}
                    isCompleted={isBasicInfoCompleted}
                />
                <OnboardingProgressStep
                    title="Experience Preferences"
                    description={getExperienceDescription()}
                    isCompleted={isExperienceCompleted}
                />
                <OnboardingProgressStep
                    title="Medical & Final Information"
                    description={getFinalDescription()}
                    isCompleted={isFinalCompleted}
                />
                <OnboardingProgressStep
                    title="Consultation Scheduled"
                    description={getConsultationDescription()}
                    isCompleted={isConsultationScheduled}
                    isLast
                />
            </div>
        </section>
    );
}
